import Vue from 'vue';
import { LoginForm } from '@/interfaces/loginInfo';
import permissions from '@/lib/permissions';
import { Commit, Dispatch } from 'vuex';

interface AuthenticationState {
	loggedIn: boolean;
}

export default {
	namespaced: true,
	state: {
		loggedIn: false,
	} as AuthenticationState,
	getters: {
		getLoggedIn: (state: AuthenticationState): boolean => {
			return state.loggedIn;
		},
	},
	mutations: {
		CHANGE_LOGGED_IN(state: AuthenticationState, status: boolean): void {
			state.loggedIn = status;
		},
		CLEAN_LOGGED_IN(state: AuthenticationState): void {
			state.loggedIn = false;
		},
	},
	actions: {
		async logIn(
			/* eslint-disable  @typescript-eslint/no-explicit-any */
			{ dispatch, rootGetters }: { dispatch: Dispatch; rootGetters: any },
			{ loginForm }: { loginForm: LoginForm }
		): Promise<void> {
			const resp = await Vue.axios
				.get(`${process.env.VUE_APP_API_URL}/token`, {
					headers: {
						Authorization: `Basic ${btoa(`${loginForm.username}:${loginForm.password}`)}`,
					},
				})
				.catch(() => {
					throw new Error('Login failed');
				});

			const token = `Basic ${btoa(`${resp.data.token}:`)}`;

			Vue.axios.defaults.headers.common['Authorization'] = token;

			await dispatch('usermanagement/user/fetchUser', { id: resp.data.id }, { root: true });

			if (!permissions.checkClientSitePermission(rootGetters['usermanagement/user/getUser'])) {
				dispatch('usermanagement/cleanFullUser', {}, { root: true });
				throw new Error('Missing permissions');
			}
			Vue.$cookies.set('Global-Token', { id: resp.data.id, token: token }, '4h');
			Vue.$cookies.set('Token', { id: resp.data.id, token: token }, '4h');

			dispatch('changeLoginStatus', { status: true });

			await dispatch('tenancy/fetchAllTenancy', null, { root: true });
		},

		logOut({ dispatch, commit }: { dispatch: Dispatch; commit: Commit }): void {
			try {
				dispatch('cleanAllData', null, { root: true });
				Vue.$cookies.remove('Token');
				Vue.$cookies.remove('Global-Token');
				commit('CLEAN_LOGGED_IN');
			} catch (error) {
				throw new Error(error as string);
			}
		},

		changeLoginStatus({ commit }: { commit: Commit }, { status }: { status: boolean }): void {
			commit('CHANGE_LOGGED_IN', status);
		},

		fetchGlobalAuthentication({ dispatch }: { dispatch: Dispatch }): void {
			dispatch('deleteAuthentication');
			Vue.axios.defaults.headers.common['Authorization'] = Vue.$cookies.get('Global-Token').token;
		},

		async fetchAuthentication({ dispatch }: { dispatch: Dispatch }): Promise<void> {
			dispatch('deleteAuthentication');
			Vue.axios.defaults.headers.common['Authorization'] = Vue.$cookies.get('Token').token;
		},

		deleteAuthentication(): void {
			Vue.axios.defaults.headers.common['Authorization'] = '';
		},

		async fetchGlobalToken(): Promise<void> {
			let resp;
			try {
				resp = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/token`);
			} catch (error) {
				throw new Error(error as string);
			}
			const token = `Basic ${btoa(`${resp.data.token}:`)}`;

			Vue.$cookies.set('Global-Token', { id: resp.data.id, token: token }, '4h');
		},

		/* eslint-disable  @typescript-eslint/explicit-module-boundary-types */
		async getSiteSpecificToken(
			_: any,
			{ clientId, siteId }: { clientId: number; siteId: number }
		): Promise<void> {
			const resp = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/token/client/${clientId}/site/${siteId}`
			);

			const token = `Basic ${btoa(`${resp.data.token}:`)}`;
			Vue.axios.defaults.headers.common['Authorization'] = token;

			Vue.$cookies.set('Token', { id: resp.data.id, token: token }, '4h');
		},
	},
};
